import React, { useMemo, useState } from 'react';
import { ItemLineDTO, ProductDTO } from '../domain/models';
import { Button } from './Button';
import { Tooltip } from './Tooltip';

export type ItemsTableProps = {
  items: ItemLineDTO[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, patch: Partial<ItemLineDTO> | { product: Partial<ProductDTO> }) => void;
  products?: ProductDTO[];
  onOpenProductPrices?: () => void;
  onSaveProduct?: (product: ProductDTO) => Promise<void> | void;
};

export function ItemsTable({ items, onAdd, onRemove, onUpdate, products, onOpenProductPrices, onSaveProduct }: ItemsTableProps) {
  const [savingIdx, setSavingIdx] = useState<number | null>(null);

  // Build a datalist for quick selection by product key, showing name as label
  const productOptions = useMemo(() => (products || []).map((p) => ({
    key: p.productKey || '',
    label: [p.name, p.unit].filter(Boolean).join(' · '),
    product: p,
  })).filter(o => o.key), [products]);

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6 overflow-x-auto" data-testid="items-table">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">Positionen</h2>
        <div className="flex gap-2">
          {onOpenProductPrices && (
            <Tooltip content="Artikelpreise öffnen">
              <Button variant="secondary" onClick={onOpenProductPrices} data-testid="open-product-prices-btn">Artikelpreise verwalten</Button>
            </Tooltip>
          )}
          <Tooltip content="Neue Position hinzufügen">
            <Button variant="primary" onClick={onAdd} data-testid="add-item-btn">+ Position</Button>
          </Tooltip>
        </div>
      </div>

      {/* Datalist for product key lookup */}
      <datalist id="product-keys" data-testid="product-keys-datalist">
        <option value="" label="(Kein Schlüssel)" data-testid="empty-product-key-option" />
        {productOptions.map((o, i) => (
          // value is key; label shows product name/unit
          <option key={o.key || i} value={o.key} label={o.label} />
        ))}
      </datalist>

      <table className="w-full text-sm">
        <thead>
        <tr className="text-left text-slate-600 border-b">
          <th className="p-2">Beschreibung</th>
          <th className="p-2 w-28">Einheit</th>
          <th className="p-2 w-24">Menge</th>
          <th className="p-2 w-28">Einzelpreis (€)</th>
          <th className="p-2 w-24">Summe</th>
          <th className="p-2 w-10"></th>
        </tr>
        </thead>
        <tbody>
        {items.length === 0 && (
          <tr>
            <td className="p-3 text-slate-500" colSpan={6}>Keine Positionen. Fügen Sie eine Position hinzu.</td>
          </tr>
        )}
        {items.map((it, idx) => {
          const lineTotal = Number(it.product.unitPrice || 0) * Number(it.quantity || 0);
          const saveDisabled = savingIdx === idx;
          const onSave = async () => {
            if (!onSaveProduct) return;
            try {
              setSavingIdx(idx);
              await onSaveProduct({ ...it.product, unitPrice: Number(it.product.unitPrice || 0) });
            } finally {
              setSavingIdx(null);
            }
          };
          return (
            <tr key={idx} className="border-b last:border-0" data-testid={`item-row-${idx}`}>
              <td className="p-2">
                {/* Dropdown selection by product key from API (with manual override) */}
                <input
                  list="product-keys"
                  className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  placeholder="Produkt-Schlüssel auswählen/eingeben"
                  value={it.product.productKey || ''}
                  onChange={(e) => {
                    const key = e.target.value;
                    if (!key.trim()) {
                      // Leerauswahl: Produkt-Schlüssel entfernen und Produkt-ID zurücksetzen
                      onUpdate(idx, { product: { productKey: undefined, id: undefined } });
                      return;
                    }
                    onUpdate(idx, { product: { productKey: key } });
                    const match = (products || []).find(p => (p.productKey || '').toLowerCase() === key.trim().toLowerCase());
                    if (match) {
                      onUpdate(idx, { product: { id: match.id, name: match.name, unit: match.unit, unitPrice: match.unitPrice, description: match.description } });
                    }
                  }}
                  onBlur={(e) => {
                    const key = e.currentTarget.value;
                    if (!key.trim()) {
                      onUpdate(idx, { product: { productKey: undefined } });
                      return;
                    }
                    const match = (products || []).find(p => (p.productKey || '').toLowerCase() === key.trim().toLowerCase());
                    if (match) {
                      onUpdate(idx, { product: { id: match.id, name: match.name, unit: match.unit, unitPrice: match.unitPrice, description: match.description } });
                    }
                  }}
                />
                {/* Manual name/description input still allowed */}
                <input className="border border-slate-300 rounded px-3 py-2 w-full mt-2 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" placeholder="Name/Beschreibung" value={it.product.name || ''} onChange={(e) => onUpdate(idx, { product: { name: e.target.value } })} />
              </td>
              <td className="p-2">
                <select className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={it.product.unit || 'Stk.'} onChange={(e) => onUpdate(idx, { product: { unit: e.target.value as any } })}>
                  <option value="Stk.">Stk.</option>
                  <option value="kg">kg</option>
                  <option value="to.">to.</option>
                  <option value="h">h</option>
                </select>
              </td>
              <td className="p-2">
                <input type="number" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" min={0} step={1} value={it.quantity as number} onChange={(e) => onUpdate(idx, { quantity: Number(e.target.value) })} />
              </td>
              <td className="p-2">
                <input type="number" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" min={0} step={0.01} value={Number(it.product.unitPrice || 0)} onChange={(e) => onUpdate(idx, { product: { unitPrice: Number(e.target.value) } })} />
              </td>
              <td className="p-2 whitespace-nowrap">{lineTotal.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
              <td className="p-2 text-right" data-testid={`item-actions-${idx}`}>
                <div className="flex flex-col items-end gap-2">
                  <Tooltip content="Position entfernen (wird sofort entfernt)">
                    <Button variant="danger" onClick={() => onRemove(idx)} data-testid={`remove-item-${idx}`}>Entfernen</Button>
                  </Tooltip>
                  {onSaveProduct && (
                    <Tooltip content="Produkt speichern (Preis aktualisieren)">
                      <Button variant="secondary" disabled={saveDisabled} loading={saveDisabled} onClick={onSave} data-testid={`save-item-${idx}`}>
                        {saveDisabled ? 'Speichern…' : 'Speichern'}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
        </tbody>
      </table>
    </div>
  );
}
